import {
  Box,
  Heading,
  Image,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CodesetSelect from "./CodesetSelect";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";

const DirectoryEditor = ({ party, onClose, isNew }) => {
  const [selectedParty, setSelectedParty] = useState({});
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (party) {
      setSelectedParty({ ...party });
    }
  }, [party]);

  const handleChange = (field, value) => {
    setSelectedParty((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (section, field, value) => {
    setSelectedParty((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const updateIdentityField = (index, field, value) => {
    setSelectedParty((prev) => {
      const updated = [...(prev.partyIdentities || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, partyIdentities: updated };
    });
  };

  const addIdentity = () => {
    setSelectedParty((prev) => ({
      ...prev,
      partyIdentities: [
        ...(prev.partyIdentities || []),
        { id: "", agencyCode: "" },
      ],
    }));
  };

  const removeIdentity = (index) => {
    setSelectedParty((prev) => {
      const updated = [...(prev.partyIdentities || [])];
      updated.splice(index, 1);
      return { ...prev, partyIdentities: updated };
    });
  };

  const handleSave = async () => {
    try {
      const response = await fetch(
        isNew
          ? "https://directory.poc.mrc.ai/api/parties"
          : `https://directory.poc.mrc.ai/api/parties/${selectedParty._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(selectedParty),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to ${isNew ? "create" : "save"} party. Status: ${
            response.status
          }`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Party created" : "Party saved",
        description: isNew
          ? "New party was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/party/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving party:", error);

      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving the party.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    navigate(`/Poc`);
  };

  return (
    <Box>
      <Navbar />

      {/* Watermark background */}
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        {/* Overlay content */}
        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit Party</Heading>
                <Tooltip label="Save Party" aria-label="Save Party Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>
          <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>Header</Tab>
              <Tab>Identities</Tab>
              <Tab>Address</Tab>
              <Tab>Contact</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  flex={1}
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <FormControl>
                    <FormLabel>Id</FormLabel>
                    <Input readOnly
                      value={selectedParty._id || ""}
                      onChange={(e) => handleChange("_id", e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={selectedParty.name || ""}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </FormControl>

                  <CodesetSelect
                    label="Party Type"
                    codesetCode="3035"
                    value={
                      selectedParty.type
                        ? { code: selectedParty.type, description: "" }
                        : null
                    }
                    onChange={(val) => handleChange("type", val.code)}
                  />

                  <CodesetSelect
                    label="Role Code"
                    codesetCode="3035"
                    value={
                      selectedParty.roleCode
                        ? { code: selectedParty.roleCode, description: "" }
                        : null
                    }
                    onChange={(val) => handleChange("roleCode", val.code)}
                  />
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <HStack mb={2}>
                    <Heading size="sm">Party Identities</Heading>
                    <IconButton
                      icon={<HiOutlinePlus />}
                      size="sm"
                      onClick={addIdentity}
                    />
                  </HStack>
                  {(selectedParty.partyIdentities || []).map(
                    (identity, idx) => (
                      <HStack key={idx} spacing={2} mb={2}>
                        <CodesetSelect
                          codesetCode="3055"
                          value={
                            identity.agencyCode
                              ? { code: identity.agencyCode, description: "" }
                              : null
                          }
                          onChange={(val) =>
                            updateIdentityField(idx, "agencyCode", val.code)
                          }
                          size="sm"
                        />
                        <Input
                          size="sm"
                          value={identity.id || ""}
                          onChange={(e) =>
                            updateIdentityField(idx, "id", e.target.value)
                          }
                        />
                        <IconButton
                          icon={<HiOutlineTrash />}
                          size="sm"
                          onClick={() => removeIdentity(idx)}
                        />
                      </HStack>
                    )
                  )}
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  flex={1}
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <CodesetSelect
                    label="Type"
                    codesetCode="3299"
                    value={
                      selectedParty.address?.type
                        ? { code: selectedParty.address.type, description: "" }
                        : null
                    }
                    onChange={(val) =>
                      handleNestedChange("address", "type", val.code)
                    }
                  />
                  <FormControl>
                    <FormLabel>Number and Street</FormLabel>
                    <Input
                      value={selectedParty.address?.numberAndStreet || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "address",
                          "numberAndStreet",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Subentity Code</FormLabel>
                    <Input
                      value={selectedParty.address?.subentityCode || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "address",
                          "subentityCode",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input
                      value={selectedParty.address?.cityName || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "address",
                          "cityName",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Postal Code</FormLabel>
                    <Input
                      value={selectedParty.address?.postalCode || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "address",
                          "postalCode",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Country Code</FormLabel>
                    <Input
                      value={selectedParty.address?.countryCode || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "address",
                          "countryCode",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  flex={1}
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Input
                      value={selectedParty.contact?.description || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "contact",
                          "description",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Role Code</FormLabel>
                    <Input
                      value={selectedParty.contact?.roleCode || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "contact",
                          "roleCode",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Person Name</FormLabel>
                    <Input
                      value={selectedParty.contact?.personName || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "contact",
                          "personName",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Telephone</FormLabel>
                    <Input
                      value={selectedParty.contact?.telephone || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          "contact",
                          "telephone",
                          e.target.value
                        )
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Fax</FormLabel>
                    <Input
                      value={selectedParty.contact?.fax || ""}
                      onChange={(e) =>
                        handleNestedChange("contact", "fax", e.target.value)
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      value={selectedParty.contact?.email || ""}
                      onChange={(e) =>
                        handleNestedChange("contact", "email", e.target.value)
                      }
                    />
                  </FormControl>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </Box>
  );
};

export default DirectoryEditor;
